/*
 * AMBRELLE · quiz « Quelle est votre heure ambrée ? » — <ambrelle-quiz>
 * Une question par écran ; routine calculée dans le navigateur (snippets/ambrelle-quiz-data.liquid, issu de content/quiz/rules.json).
 * Heure ambrée = lever du soleil de demain (calcul NOAA de assets/ambrelle-sky.js, pays lu dans window.AmbrelleSky).
 * Balisage dans les <template data-tpl> de sections/ambrelle-quiz.liquid (textes traduits côté serveur).
 * Carte 9:16 dessinée en canvas, partage natif ou téléchargement. Sans dépendance.
 * Accessibilité : vrais boutons radio (flèches + Entrée), titre focalisé à chaque écran, zone live, prefers-reduced-motion.
 */
(() => {
  if (!window.customElements || customElements.get('ambrelle-quiz')) return;

  const DAY = 864e5;
  const RAD = Math.PI / 180;
  const KEY = 'ambrelle-quiz';
  const fill = (s, o) => String(s == null ? '' : s).replace(/\{(\w+)\}/g, (m, k) => (o[k] != null ? o[k] : ''));
  const decoder = document.createElement('textarea');
  const decode = (s) => {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    decoder.innerHTML = s;
    return decoder.value;
  };
  const decodeAll = (node) => {
    if (Array.isArray(node)) return node.map(decodeAll);
    if (node && typeof node === 'object') {
      const out = {};
      for (const k in node) out[k] = decodeAll(node[k]);
      return out;
    }
    return decode(node);
  };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => '&#' + c.charCodeAt(0) + ';');
  const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  const calm = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((done) => setTimeout(done, calm() ? 0 : ms));
  const memo = (value) => {
    try {
      if (value === undefined) return JSON.parse(sessionStorage.getItem(KEY));
      sessionStorage.setItem(KEY, JSON.stringify(value));
    } catch (error) {
      return null;
    }
  };

  /* Lever et coucher du soleil pour la date UTC de t (mêmes équations que ambrelle-sky.js). */
  function sunDay(t, lat, lon) {
    const d = new Date(t);
    const y = d.getUTCFullYear();
    const mid = Date.UTC(y, d.getUTCMonth(), d.getUTCDate());
    const g = ((2 * Math.PI) / 365) * Math.floor((mid - Date.UTC(y, 0, 1)) / DAY);
    const cos = (k) => Math.cos(k * g);
    const sin = (k) => Math.sin(k * g);
    const eq = 229.18 * (7.5e-5 + 0.001868 * cos(1) - 0.032077 * sin(1) - 0.014615 * cos(2) - 0.040849 * sin(2));
    const de = 0.006918 - 0.399912 * cos(1) + 0.070257 * sin(1) - 0.006758 * cos(2) + 0.000907 * sin(2) - 0.002697 * cos(3) + 0.00148 * sin(3);
    const ph = lat * RAD;
    const c = Math.cos(90.833 * RAD) / (Math.cos(ph) * Math.cos(de)) - Math.tan(ph) * Math.tan(de);
    if (c > 1 || c < -1) return null;
    const noon = mid + (720 - 4 * lon - eq) * 6e4;
    const ha = (Math.acos(c) / RAD) * 24e4;
    return { rise: noon - ha, set: noon + ha };
  }

  class AmbrelleQuiz extends HTMLElement {
    connectedCallback() {
      const read = (s) => {
        try {
          return JSON.parse(this.$(s).textContent);
        } catch (error) {
          return null;
        }
      };
      this.R = read('[data-quiz-rules]');
      /* Les traductions arrivent échappées par le filtre t de Shopify (« d&#39;eau ») : on les décode une fois. */
      this.T = decodeAll(read('[data-quiz-i18n]'));
      this.D = read('[data-quiz-products]') || [];
      if (!this.R || !this.T || this.Q) return;
      this.Q = this.R.questions;
      this.lang = this.dataset.lang || document.documentElement.lang || 'en';
      this.map = {};
      String(read('[data-quiz-map]') || '').split(/\r?\n/).forEach((line) => {
        const pair = line.split('=');
        if (pair[1]) this.map[pair[0].trim().toUpperCase()] = pair[1].trim();
      });
      const words = String(this.dataset.keywords || '').split(',').map(norm).filter(Boolean);
      this.kw = words.length ? words : this.R.cure_keywords.map(norm);
      Object.assign(this, { a: {}, name: '', city: '' });
      ['intro', 'app', 'stage', 'bar', 'count', 'top', 'live', 'email'].forEach((k) => (this[k] = this.$(`[data-${k}]`)));

      this.addEventListener('click', (event) => {
        const el = event.target.closest('[data-go]');
        if (el) this[el.dataset.go](el);
      });
      this.stage.addEventListener('pointerdown', () => (this.ptr = true));
      this.stage.addEventListener('keydown', (event) => {
        this.ptr = false;
        if (event.key === 'Enter' && event.target.type === 'radio') {
          event.preventDefault();
          this.next();
        }
      });
      this.stage.addEventListener('change', (event) => {
        const input = event.target;
        if (input.type !== 'radio') return;
        this.a[input.name] = input.value;
        this.stage.querySelector('[type=submit]').disabled = false;
        if (this.ptr) wait(340).then(() => this.next());
        this.ptr = false;
      });
      this.stage.addEventListener('submit', (event) => {
        event.preventDefault();
        const f = event.target.elements;
        if (!f.city) return this.next();
        this.name = f.firstname.value.trim().slice(0, 24);
        this.city = f.city.value.trim().slice(0, 40);
        this.finish();
      });

      /* Retour du formulaire e-mail (rechargement de page) : on réaffiche le résultat. */
      const saved = memo();
      if (this.email && this.email.querySelector('[data-email-done]')) {
        this.email.hidden = false;
        if (saved) Object.assign(this, saved) && this.go(this.Q.length + 1, true);
        this.email.scrollIntoView({ block: 'center' });
      }
    }

    $(s, root) {
      return (root || this).querySelector(s);
    }

    /* Copie un <template data-tpl> et remplit ses [data-k] : texte, « =clé » pour du HTML, « ?clé » pour afficher / masquer. */
    tpl(name, data) {
      const node = this.$(`template[data-tpl="${name}"]`).content.cloneNode(true);
      node.querySelectorAll('[data-k]').forEach((el) => {
        const key = el.dataset.k;
        const value = data[key.replace(/^[=?]/, '')];
        el.hidden = value == null || value === '';
        if (el.hidden || key[0] === '?') return;
        if (key[0] === '=') el.innerHTML = value;
        else el.textContent = value;
      });
      return node;
    }

    start() {
      this.go(0);
    }

    back() {
      this.go(this.i - 1);
    }

    restart() {
      this.a = {};
      this.name = this.city = '';
      memo(null);
      this.go(0);
    }

    next() {
      const q = this.Q[this.i];
      if (q && this.a[q.id]) this.go(this.i + 1);
    }

    async go(i, instant) {
      if (this.busy) return;
      this.busy = true;
      if (!instant) {
        this.classList.add('is-leaving');
        await wait(220);
      }
      this.i = i;
      this.intro.hidden = i >= 0;
      this.app.hidden = i < 0;
      if (i >= 0) this.render();
      this.classList.remove('is-leaving');
      this.busy = false;
      const target = i < 0 ? this.$('[data-go=start]') : this.$('[data-focus]', this.stage);
      if (target) target.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top;
      if (!instant && (top < 0 || top > innerHeight / 2)) this.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth' });
    }

    render() {
      const i = this.i;
      const n = this.Q.length;
      this.top.hidden = i > n;
      this.dataset.screen = i > n ? 'result' : 'question';
      this.bar.style.setProperty('--p', Math.min(i + 1, n + 1) / (n + 1));
      this.count.textContent = i < n ? fill(this.T.nav.progress, { current: i + 1, total: n }) : this.T.nav.final;
      if (i > n) return this.result();
      this.stage.replaceChildren(i < n ? this.question(i) : this.nameStep());
    }

    question(i) {
      const q = this.Q[i];
      const t = this.T.questions[q.id];
      const node = this.tpl('question', { title: t.title, hint: t.hint, note: i === 1 && this.a.wake ? this.gapText() : '' });
      const set = this.$('fieldset', node);
      const id = this.id + q.id;
      this.$('h2', node).id = id;
      this.$('.aq-hint', node).id = id + 'h';
      set.setAttribute('aria-labelledby', id);
      set.setAttribute('aria-describedby', id + 'h');
      q.answers.forEach((value, k) => {
        const opt = this.tpl('option', { k: String.fromCharCode(65 + k), label: t.answers[value] });
        const input = this.$('input', opt);
        Object.assign(input, { name: q.id, value, checked: this.a[q.id] === value });
        set.append(opt);
      });
      this.$('[type=submit]', node).disabled = !this.a[q.id];
      return node;
    }

    nameStep() {
      const place = this.place();
      const node = this.tpl('name', { hint: place.name ? fill(this.T.name.city_hint, { city: place.name }) : '' });
      const f = this.$('form', node).elements;
      f.firstname.value = this.name;
      f.city.value = this.city;
      f.city.placeholder = place.name;
      (this.country() || { cities: [] }).cities.forEach((c) => this.$('datalist', node).append(new Option(this.cityName(c))));
      return node;
    }

    /* Lieu : pays du marché (window.AmbrelleSky), ville saisie si elle est connue, sinon la capitale. */
    country() {
      const sky = window.AmbrelleSky && window.AmbrelleSky.state;
      return this.R.places[String((sky && sky.country) || this.dataset.country).toUpperCase()];
    }

    cityName(c) {
      return (c[3] && c[3][this.lang]) || c[0];
    }

    place() {
      const list = this.country();
      if (list) {
        const typed = norm(this.city);
        const c = (typed && list.cities.find((city) => [city[0]].concat(Object.values(city[3] || {})).some((n) => norm(n) === typed))) || list.cities[0];
        return { name: this.cityName(c), lat: c[1], lon: c[2], tz: list.tz };
      }
      const sky = window.AmbrelleSky && window.AmbrelleSky.state;
      const p = (sky && sky.place) || { lat: 48.5, lon: -new Date().getTimezoneOffset() / 4 };
      return { name: '', lat: p.lat, lon: p.lon };
    }

    hm(t, tz) {
      try {
        return new Intl.DateTimeFormat(this.lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz }).format(t);
      } catch (error) {
        return new Date(t).toTimeString().slice(0, 5);
      }
    }

    sun() {
      const p = this.place();
      const now = Date.now() + DAY;
      const day = sunDay(now, p.lat, p.lon) || { rise: now, set: now + 432e5 };
      const rise = this.hm(day.rise, p.tz);
      return Object.assign(p, { rise, set: this.hm(day.set, p.tz), min: +rise.slice(0, 2) * 60 + +rise.slice(3, 5) });
    }

    gap() {
      return this.sun().min - (this.R.wake_minutes[this.a.wake] || 420);
    }

    gapText() {
      const g = this.gap();
      const t = this.T.gap;
      const m = Math.round(Math.abs(g) / 5) * 5;
      const h = Math.floor(m / 60);
      const r = m % 60;
      if (m < 10) return t.same;
      const span = h ? (r ? fill(t.hm, { h, m: r < 10 ? '0' + r : r }) : fill(t.h, { h })) : fill(t.min, { m });
      return fill(g > 0 ? t.before : t.after, { gap: span });
    }

    match(when) {
      return Object.keys(when).every((k) => (k === 'before_sun' ? when[k] === this.gap() > 0 : this.a[k] === when[k]));
    }

    find(key) {
      const info = this.R.products[key];
      const handle = this.map[key.toUpperCase()] || (info ? info.handle : key);
      return this.D.find((p) => p.h === handle) || (info && this.D.find((p) => p.v.some((v) => String(v.s).toUpperCase().indexOf(key) === 0)));
    }

    isCure(prod, v) {
      const label = norm(prod.fo > 0 ? v.o[prod.fo - 1] : v.o.join(' '));
      return this.kw.some((k) => label.indexOf(k) > -1);
    }

    /* Variante : cure 3 mois si le pot dure moins de 90 jours (offre « cure »), sinon le pot. */
    pick(prod, want) {
      const ok = prod.v.filter((v) => v.a);
      const pool = ok.length ? ok : prod.v;
      return pool.find((v) => this.isCure(prod, v) === want) || pool[want ? pool.length - 1 : 0];
    }

    /* Routine : étapes de rules.json dans l'ordre, équivalent selon le régime, limite, coffret le plus proche, profil. */
    compute() {
      const R = this.R;
      const a = this.a;
      const hints = [];
      let list = [];
      const add = (sku, swap) => sku && !list.some((x) => x.sku === sku) && list.push({ sku, swap });
      R.steps.forEach((step) => {
        if (!this.match(step.when)) return;
        let sku = step.pick[a.format] || step.pick['*'];
        const info = R.products[sku];
        const swap = (a.diet === 'vegan' && !info.vegan) || (a.diet === 'vegetarian' && !info.vegetarian);
        if (swap) sku = R.swaps[sku];
        if (sku) hints.push(...(step.bundle_hint || []));
        add(sku, swap);
      });
      list = list.map((x) => {
        const info = R.products[x.sku];
        const prod = this.find(x.sku);
        const cure = prod && info.offer === 'cure' && prod.v.length > 1;
        return prod && Object.assign(x, { info, prod, cure, v: this.pick(prod, cure) });
      }).filter(Boolean);
      const firstOf = (routine) => list.find((x) => x.info.routine === routine);
      let main = [firstOf('aube'), firstOf('crepuscule')].filter(Boolean);
      if (main.length < (R.max_products || 2)) main = main.concat(list.filter((x) => !main.includes(x))).slice(0, R.max_products || 2);
      const more = list.filter((x) => !main.includes(x)).slice(0, R.max_more || 0);
      list = main;
      let best = null;
      for (const k in R.bundles) {
        const b = R.bundles[k];
        const hit = b.skus.filter((s) => list.some((x) => x.sku === s)).length;
        const score = hit * 10 - (b.skus.length - hit) * 3 + (hints.includes(k) ? 4 : 0);
        const prod = this.find(k);
        if (prod && hit > 1 && (!best || score > best.score)) best = { k, b, hit, score, prod };
      }
      return { list, more, bundle: best, profile: R.profiles.find((p) => this.match(p.when)) };
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.lang, { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2);
      }
    }

    days(x) {
      return x.cure ? x.info.cure_days : x.info.days;
    }

    perDay(x) {
      const d = this.days(x);
      return d ? Math.round(x.v.p / d) : 0;
    }

    /* Visuel livré avec le thème : packshot studio (amb-p-<sku>-1) ; visuel du coffret (amb-pack-<handle>). */
    image(img, prod, key) {
      const isSku = !!this.R.products[key];
      img.src = prod.i || this.dataset.assets + (isSku ? 'amb-p-' + key.toLowerCase() + '-1-400.webp' : 'amb-pack-' + key.toLowerCase() + '-400.webp');
    }

    item(x, R, moments) {
      const info = x.info;
      const months = x.cure ? Math.round(info.cure_days / 30) : 0;
      const C = (R.containers && R.containers[info.container]) || R;
      const lasts = x.cure ? fill(C.lasts_cure, { months, jars: info.cure_jars, days: info.cure_days }) : fill(C.lasts_pot, { days: info.days });
      const node = this.tpl('item', {
        moment: moments[info.routine === 'aube' ? 0 : 1],
        title: x.prod.t,
        why: x.swap ? R.swap_note : '',
        lasts,
        sub: info.sub_months ? fill(x.cure ? C.sub_cure : C.sub_pot, { months: info.sub_months }) : '',
        perday: x.v.a && this.perDay(x) ? fill(R.per_day, { price: this.money(this.perDay(x)) }) : '',
        price: x.v.a ? this.money(x.v.p) : R.unavailable,
        addone: x.v.a ? fill(R.add_one, { price: this.money(x.v.p) }) : ''
      });
      this.$('li', node).classList.add('aq-item--' + info.routine);
      node.querySelectorAll('a').forEach((a) => (a.href = x.prod.u));
      const btn = this.$('[data-go=add]', node);
      if (btn) btn.dataset.sku = x.sku;
      this.image(this.$('img', node), x.prod, x.sku);
      return node;
    }

    result() {
      const T = this.T;
      const R = T.result;
      const s = (this.s = this.sun());
      const out = (this.out = this.compute());
      const prof = out.profile;
      const P = T.profiles[prof.id];
      const name = esc(this.name);
      const tpl = R[name ? (s.name ? 'title' : 'title_nocity') : s.name ? 'title_noname' : 'title_plain'];
      const moments = [R.moment_aube, R.moment_crepuscule];
      const short = moments.map((m) => m.split(' · ')[0]);
      const cures = out.list.filter((x) => x.cure).length;
      const b = out.bundle;
      const node = this.tpl('result', {
        title: fill(esc(tpl), { name, time: `<span class="aq-time">${s.rise}</span>`, city: `<span class="aq-city">${esc(s.name)}</span>` }),
        aube: short[0],
        dusk: short[1],
        rise: s.rise,
        set: s.set,
        gap: this.gapText(),
        note: s.name ? fill(R.sun_note, { city: s.name }) : R.sun_note_plain,
        pname: P.name,
        pline: P.line,
        ptext: P.text,
        buy: out.list.some((x) => x.v.a) ? 1 : '',
        total: (() => {
          const ok = out.list.filter((x) => x.v.a);
          return fill(ok.length === 1 ? R.add_total_one : R.add_total, {
            count: ok.length,
            amount: this.money(ok.reduce((sum, x) => sum + x.v.p, 0)),
            total: this.money(ok.reduce((sum, x) => sum + this.perDay(x), 0))
          });
        })(),
        more: out.more.length ? fill(R.more, { count: out.more.length }) : '',
        offer: cures ? (cures === out.list.length ? R.offer_cure : R.offer_mix) : '',
        bundle: b ? 1 : '',
        btext: b ? fill(R.bundle_text, { title: b.prod.t, count: b.hit, pct: b.b.discount_pct }) : '',
        share: navigator.share ? 1 : ''
      });
      const c = prof.colors;
      this.$('svg', node).setAttribute('aria-label', fill(R.arc_label, { sunrise: s.rise, sunset: s.set }));
      const hero = this.$('.aq-hero', node);
      [0, 1, 2].forEach((k) => hero.style.setProperty('--c' + k, c[k]));
      const list = this.$('.aq-list', node);
      out.list.forEach((x) => list.append(this.item(x, R, moments)));
      const moreList = this.$('.aq-more .aq-list', node);
      if (moreList) out.more.forEach((x) => moreList.append(this.item(x, R, moments)));
      if (b) {
        this.image(this.$('.aq-bundle img', node), b.prod, b.k);
        this.$('.aq-bundle a', node).href = b.prod.u;
      }
      this.$('[data-card]', node).alt = fill(T.share.card_alt, { profile: P.name, time: s.rise });
      if (!navigator.share) this.$('[data-go=download]', node).classList.remove('aq-btn--ghost');
      this.dataset.profile = prof.id;
      node.querySelectorAll('[data-go=share], [data-go=download]').forEach((b) => (b.disabled = true));
      this.stage.replaceChildren(node);
      if (this.email) {
        this.$('[data-email-slot]', this.stage).append(this.email);
        this.email.hidden = false;
        const field = (n) => this.email.querySelector(`[name="contact[${n}]"]`) || {};
        const box = this.email.querySelector('[data-consent]');
        const submit = this.email.querySelector('[type=submit]');
        const sync = () => {
          const ok = !!(box && box.checked);
          field('tags').value = 'quiz,' + prof.id.replace(/_/g, '-') + (ok ? ',newsletter' : '');
          field('accepts_marketing').value = ok ? 'true' : 'false';
          if (submit) submit.disabled = !ok;
        };
        if (box && !box.dataset.bound) {
          box.dataset.bound = '1';
          box.addEventListener('change', sync);
          this.email.querySelector('form')?.addEventListener('submit', (event) => {
            if (!box.checked) event.preventDefault();
          });
        }
        sync();
        field('first_name').value = this.name;
      }
      memo({ a: this.a, name: this.name, city: this.city });
      this.live.textContent = fill(tpl, { name: this.name, time: s.rise, city: s.name });
      this.card(s, prof, P).catch(() => {});
    }

    async add(button) {
      const R = this.T.result;
      const one = button.dataset.sku;
      const msg = one ? null : this.$('[data-add-msg]', this.stage);
      const pool = one ? this.out.list.concat(this.out.more).filter((x) => x.sku === one) : this.out.list;
      const items = pool.filter((x) => x.v.a).map((x) => ({ id: x.v.id, quantity: 1, properties: { _quiz: this.out.profile.id } }));
      if (!items.length) return;
      const label = button.textContent;
      const say = (text) => {
        if (msg) msg.textContent = text;
        else this.live.textContent = text;
      };
      button.disabled = true;
      say(R.adding);
      let Ev = null;
      try {
        Ev = (await import('@shopify/events')).CartLinesUpdateEvent;
      } catch (error) {}
      const drawer = document.querySelector('cart-drawer-component');
      const deferred = Ev && drawer ? Ev.createPromise() : null;
      if (deferred) {
        this.dispatchEvent(
          new Ev({ action: 'add', context: 'product', lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })), promise: deferred.promise })
        );
      }
      const sections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean)
        .join(',');
      try {
        const response = await fetch(this.dataset.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(sections ? { items, sections } : { items })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || response.status);
        say(one ? R.added_one : R.added);
        button.classList.add('is-done');
        if (!deferred) {
          await wait(500);
          location.href = this.dataset.cartUrl;
          return;
        }
        const cart = await (await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: Ev.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'ambrelle-quiz', sourceId: this.id, itemCount: items.length, sections: data.sections, didError: false }
        });
        await wait(1200);
        button.disabled = false;
        button.classList.remove('is-done');
        button.textContent = label;
      } catch (error) {
        say(R.add_error);
        if (deferred) {
          try {
            const cart = await (await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
            deferred.resolve({ cart: Ev.createCartFromAjaxResponse(cart), detail: { items: cart.items, didError: true, source: 'ambrelle-quiz', sourceId: this.id } });
          } catch (fetchError) {
            deferred.reject(fetchError);
          }
        }
        button.disabled = false;
      }
    }

    /* Carte 9:16 (1080 × 1920) : ciel du profil, soleil à l'horizon, arc de la journée, heure, ville, profil, logo. */
    async card(s, prof, P) {
      const W = 1080;
      const H = 1920;
      const HZ = 1290;
      const M = W / 2;
      const IV = 'rgba(246,239,228,';
      const cv = document.createElement('canvas');
      const x = cv.getContext('2d');
      const css = getComputedStyle(this);
      const serif = css.getPropertyValue('--aq-serif').trim() || 'serif';
      const sans = css.getPropertyValue('--aq-sans').trim() || 'sans-serif';
      const sh = this.T.share;
      const c = prof.colors;
      cv.width = W;
      cv.height = H;
      try {
        await Promise.all([`300px ${serif}`, `italic 100px ${serif}`, `500 40px ${sans}`, `40px ${sans}`].map((f) => document.fonts.load(f)));
      } catch (error) {}
      const put = (text, y, size, family, alpha, o) => {
        o = o || {};
        x.fillStyle = IV + alpha + ')';
        x.textAlign = o.align || 'center';
        x.letterSpacing = (o.ls || 0) + 'px';
        do x.font = `${o.style || ''} ${o.weight || 400} ${size}px ${family}`;
        while (x.measureText(text).width > 940 && (size -= 4) > 16);
        x.fillText(text, o.x || M, y);
      };
      const grad = (g, stops) => {
        stops.forEach((st, k) => g.addColorStop(st, [c[0], c[1], c[2], 'rgba(252,227,180,.8)', 'rgba(227,169,75,.32)', 'rgba(227,169,75,0)', '#120C08', '#FFF4DE', '#F8CF8C', '#E9AE55'][k]));
        x.fillStyle = g;
      };
      const disc = (cx, r) => {
        x.beginPath();
        x.arc(cx, HZ, r, 0, 7);
        x.fill();
      };
      grad(x.createLinearGradient(0, 0, 0, HZ), [0, 0.58, 1]);
      x.fillRect(0, 0, W, HZ);
      grad(x.createRadialGradient(M, HZ, 0, M, HZ, 820), [, , , 0, 0.3, 1]);
      x.fillRect(0, 0, W, HZ);
      grad(x.createLinearGradient(0, HZ, 0, H), [0.3, 0, , , , , 1]);
      x.fillRect(0, HZ, W, H - HZ);
      x.save();
      x.beginPath();
      x.rect(0, 0, W, HZ);
      x.clip();
      grad(x.createRadialGradient(M, HZ, 0, M, HZ, 168), [, , , , , , , 0, 0.65, 1]);
      disc(M, 168);
      x.restore();
      x.fillStyle = '#E3A94B';
      for (let k = 0; k < 5; k++) {
        x.globalAlpha = 0.55 - k * 0.1;
        x.fillRect(M - 120 + k * 22, HZ + 22 + k * 22, 240 - k * 44, 4);
      }
      x.globalAlpha = 1;
      x.fillStyle = IV + '.4)';
      x.fillRect(0, HZ - 1, W, 2);
      x.strokeStyle = IV + '.6)';
      x.lineWidth = 4;
      x.lineCap = 'round';
      x.setLineDash([1, 16]);
      x.beginPath();
      x.ellipse(M, HZ, 420, 380, 0, Math.PI, 0);
      x.stroke();
      x.fillStyle = '#FCE3B4';
      disc(120, 15);
      x.fillStyle = IV + '1)';
      disc(960, 10);
      /* Nuit : croissant de lune dans le ciel de la carte (le soleil à l'horizon reste celui du lever de demain). */
      if (document.documentElement.getAttribute('data-sky') === 'nuit') {
        x.save();
        x.beginPath();
        x.arc(880, 250, 46, 0, 2 * Math.PI);
        x.clip();
        x.fillStyle = '#F6EFE4';
        x.beginPath();
        x.arc(880, 250, 46, 0, 2 * Math.PI);
        x.moveTo(944, 236);
        x.arc(902, 236, 42, 0, 2 * Math.PI);
        x.fill('evenodd');
        x.restore();
      }
      const names = [this.T.result.moment_aube, this.T.result.moment_crepuscule].map((m) => m.split(' · ')[0].toUpperCase());
      put(names[0] + '  ' + s.rise, HZ + 62, 26, sans, 0.88, { align: 'left', x: 92, ls: 4, weight: 500 });
      put(names[1] + '  ' + s.set, HZ + 62, 26, sans, 0.7, { align: 'right', x: 988, ls: 4, weight: 500 });
      put(sh.card_eyebrow.toUpperCase(), 390, 34, sans, 0.82, { ls: 9, weight: 500 });
      put(s.rise, 700, 330, serif, 1, { ls: -4 });
      put(s.name ? fill(sh.card_place, { city: s.name }) : sh.card_place_plain, 800, 46, sans, 0.86);
      if (this.name) put(this.name.toUpperCase(), 1462, 32, sans, 0.68, { ls: 9, weight: 500 });
      put(P.name, 1572, 116, serif, 1, { style: 'italic' });
      x.font = `44px ${sans}`;
      const lines = [''];
      P.line.split(' ').forEach((word) => {
        const k = lines.length - 1;
        if (lines[k] && x.measureText(lines[k] + ' ' + word).width > 900) lines.push(word);
        else lines[k] += (lines[k] ? ' ' : '') + word;
      });
      lines.forEach((line, k) => put(line, 1652 + k * 58, 44, sans, 0.8));
      put(sh.card_footer, 1820, 36, serif, 0.72, { style: 'italic' });
      put(this.dataset.cardUrl || '', 1870, 26, sans, 0.5, { ls: 6, weight: 500 });
      const logo = this.$('[data-logo]');
      if (logo) {
        const url = URL.createObjectURL(new Blob([logo.innerHTML.replace(/<rect width="100%"[^>]*>(<\/rect>)?/, '')], { type: 'image/svg+xml' }));
        const img = new Image();
        img.src = url;
        try {
          await img.decode();
          x.drawImage(img, M - 170, 120, 340, (340 * img.naturalHeight) / img.naturalWidth);
        } catch (error) {}
        URL.revokeObjectURL(url);
      }
      this.blob = await new Promise((done) => cv.toBlob(done));
      URL.revokeObjectURL(this.url);
      this.url = URL.createObjectURL(this.blob);
      const preview = this.$('[data-card]', this.stage);
      preview.src = this.url;
      preview.hidden = false;
      this.stage.querySelectorAll('[data-go=share], [data-go=download]').forEach((b) => (b.disabled = false));
    }

    async share() {
      const sh = this.T.share;
      const file = new File([this.blob], sh.file_name + '.png', { type: 'image/png' });
      const text = fill(sh.share_text, { time: this.s.rise });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], text });
        else await navigator.share({ text, url: location.href.split('?')[0] });
      } catch (error) {}
    }

    download() {
      const link = document.createElement('a');
      link.href = this.url;
      link.download = this.T.share.file_name + '.png';
      document.body.append(link);
      link.click();
      link.remove();
    }

    /* Court temps de calcul mis en scène (supprimé si mouvement réduit). */
    async finish() {
      if (!calm()) {
        this.top.hidden = true;
        this.stage.replaceChildren(this.tpl('loading', {}));
        this.$('[data-focus]', this.stage).focus({ preventScroll: true });
        await wait(1300);
      }
      this.go(this.Q.length + 1, true);
    }
  }

  customElements.define('ambrelle-quiz', AmbrelleQuiz);
})();
